"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { siteConfig } from '@/lib/site.config';

const STORAGE_KEY = siteConfig.cookieStorageKey;
const COOKIE_BANNER_HEIGHT_VAR = "--cookie-banner-height";
const REOPEN_EVENT = "cookie-consent-reopen";

interface CookieBannerProps {
  enabled?: boolean | null;
  message?: string | null;
  cookiePolicyUrl?: string | null;
}

export function CookieBanner({
  enabled = false,
  message,
  cookiePolicyUrl,
}: CookieBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing visibility from localStorage on mount is the intended pattern
      setIsVisible(false);
      return;
    }

    const stored = window.localStorage.getItem(STORAGE_KEY);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing visibility from localStorage on mount is the intended pattern
    if (!stored) setIsVisible(true);

    const onReopen = () => setIsVisible(true);
    window.addEventListener(REOPEN_EVENT, onReopen as EventListener);
    return () => {
      window.removeEventListener(REOPEN_EVENT, onReopen as EventListener);
    };
  }, [enabled]);

  useEffect(() => {
    const root = document.documentElement;

    if (!enabled || !isVisible) {
      root.style.setProperty(COOKIE_BANNER_HEIGHT_VAR, "0px");
      return;
    }

    const element = bannerRef.current;
    if (!element) return;

    const updateHeight = () => {
      root.style.setProperty(COOKIE_BANNER_HEIGHT_VAR, `${element.offsetHeight}px`);
    };

    updateHeight();

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(element);

    return () => {
      observer.disconnect();
      root.style.setProperty(COOKIE_BANNER_HEIGHT_VAR, "0px");
    };
  }, [enabled, isVisible]);

  if (!enabled || !isVisible) return null;

  const bannerMessage =
    message?.trim() ||
    "We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.";
  const policyLink = cookiePolicyUrl?.trim();

  const handleChoice = (value: "accepted" | "rejected") => {
    window.localStorage.setItem(STORAGE_KEY, value);
    window.dispatchEvent(
      new CustomEvent("cookie-consent-changed", { detail: value })
    );
    setIsVisible(false);
  };

  return (
    <div
      ref={bannerRef}
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4"
      role="region"
      aria-label="Cookie consent"
    >
      <div className="mx-auto max-w-5xl border-[0.5px] border-white bg-neutral-950/95 backdrop-blur">
        <div className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between md:gap-6 md:p-6">
          <div className="text-sm text-white/90">
            <p>{bannerMessage}</p>
            {policyLink && (
              <div className="mt-2">
                {policyLink.startsWith("/") ? (
                  <Link
                    href={policyLink}
                    className="text-white underline underline-offset-4 hover:text-white/70"
                  >
                    Cookie policy
                  </Link>
                ) : (
                  <a
                    href={policyLink}
                    className="text-white underline underline-offset-4 hover:text-white/70"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Cookie policy
                  </a>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button
              variant="cta"
              size="cta"
              className="group font-body"
              onClick={() => handleChoice("rejected")}
            >
              <span>Reject</span>
              <Plus
                className="size-[18px] shrink-0 transition-transform duration-300 group-hover:rotate-90"
                aria-hidden="true"
              />
            </Button>
            <Button
              variant="cta"
              size="cta"
              className="group font-body"
              onClick={() => handleChoice("accepted")}
            >
              <span>Accept</span>
              <Plus
                className="size-[18px] shrink-0 transition-transform duration-300 group-hover:rotate-90"
                aria-hidden="true"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
